import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp, Eye, EyeOff, Loader2, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiDelete, apiGet, apiPatch, apiPost } from "@/lib/apiAdmin";
import { HUD } from "./theme";

const err = (e) => toast.error(e?.response?.data?.detail || "Failed");
const Media = ({ it }) => (it.kind === "video"
  ? <video src={it.url} className="w-full h-32 object-cover rounded-lg" muted />
  : <img src={it.url} alt="" className="w-full h-32 object-cover rounded-lg" />);

const ShopItem = ({ it, i, n, move, reload }) => {
  const [cap, setCap] = useState(it.author_name || "");
  const patch = (b, msg) => apiPatch(`/admin/portfolio/${it.id}`, b).then(() => { toast.success(msg); reload(); }).catch(err);
  return (
    <div className={`rounded-xl border p-2 space-y-2 ${it.hidden ? "border-white/5 opacity-50" : "border-cyan-400/15"}`} data-testid={`portfolio-item-${it.id}`}>
      <Media it={it} />
      <input value={cap} placeholder="Caption (e.g. Tesla Model Y ceramic)" onChange={(e) => setCap(e.target.value)} onBlur={() => cap !== (it.author_name || "") && patch({ caption: cap }, "Caption saved")}
        className={`w-full h-8 rounded-lg px-2 text-xs border ${HUD.input}`} data-testid={`portfolio-caption-${it.id}`} />
      <div className="flex items-center gap-2 text-gray-400">
        <button disabled={i === 0} onClick={() => move(i, -1)} className="disabled:opacity-30 hover:text-white" data-testid={`portfolio-up-${it.id}`}><ArrowUp className="w-4 h-4" /></button>
        <button disabled={i === n - 1} onClick={() => move(i, 1)} className="disabled:opacity-30 hover:text-white" data-testid={`portfolio-down-${it.id}`}><ArrowDown className="w-4 h-4" /></button>
        <button onClick={() => patch({ hidden: !it.hidden }, it.hidden ? "Shown" : "Hidden")} className="hover:text-white" data-testid={`portfolio-hide-${it.id}`}>{it.hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
        <button onClick={() => window.confirm("Delete this from the portfolio?") && apiDelete(`/admin/portfolio/${it.id}`).then(reload)} className="ml-auto hover:text-rose-300" data-testid={`portfolio-delete-${it.id}`}><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
};

export const JarvisPortfolio = () => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const load = () => apiGet("/admin/portfolio").then(setD).catch(err);
  useEffect(() => { load(); }, []);
  const upload = async (files) => {
    setBusy(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/portfolio/upload", fd).catch(err);
    }
    setBusy(false);
    toast.success("Uploaded");
    load();
  };
  const move = (i, dir) => {
    const ids = d.shop.map((x) => x.id);
    [ids[i], ids[i + dir]] = [ids[i + dir], ids[i]];
    apiPost("/admin/portfolio/order", { ids }).then(load);
  };
  const toggleReview = (r) => apiPost("/admin/portfolio/review-visibility", { id: r.id, hidden: !r.hidden }).then(load);
  if (!d) return <Loader2 className="w-6 h-6 animate-spin text-cyan-300" />;
  return (
    <div className={`${HUD.panel} p-5 space-y-4`} data-testid="jarvis-portfolio">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1"><p className="text-white font-['Outfit'] text-lg">Portfolio</p><p className="text-xs text-gray-400">Your uploads show first on the Portfolio page, in this order. Review photos follow — hide any you don't want shown. Changes here save instantly (no need to Publish).</p></div>
        <input ref={input} type="file" accept="image/*,video/*" multiple hidden onChange={(e) => e.target.files.length && upload([...e.target.files])} data-testid="portfolio-file-input" />
        <button onClick={() => input.current.click()} disabled={busy} className={`${HUD.btn} h-10 px-4 text-sm flex items-center gap-2`} data-testid="portfolio-upload-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload photos / videos
        </button>
      </div>
      {d.shop.length === 0 ? <p className="text-sm text-gray-500" data-testid="portfolio-empty">No uploads yet.</p> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">{d.shop.map((it, i) => <ShopItem key={it.id} it={it} i={i} n={d.shop.length} move={move} reload={load} />)}</div>
      )}
      {d.reviews.length > 0 && <>
        <p className={HUD.label}>From reviews ({d.reviews.length})</p>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {d.reviews.map((r) => (
            <button key={r.id} onClick={() => toggleReview(r)} className={`relative rounded-lg overflow-hidden ${r.hidden ? "opacity-30" : ""}`} data-testid={`portfolio-review-${r.id}`}>
              <Media it={r} />
              <span className="absolute bottom-1 right-1 bg-black/70 rounded p-1">{r.hidden ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}</span>
            </button>
          ))}
        </div>
      </>}
    </div>
  );
};
